import React, { useEffect, useState } from 'react'
import { animateScroll as scroll } from 'react-scroll'
import {
  BlockingLoader,
  Button,
  Container,
  ExpandableButton,
  HtmlContent,
  Icon,
  Icon64,
  Image,
  Link,
  ListContainer,
  Select,
  State,
  TextBody,
  TextCaption,
  TextTitle,
  ToastContext,
  Tools
} from '@pdrj/pmi-iqos-ds-react'
import {
  CommonTranslations,
  parseTranslations,
  registerForm,
  registerParam,
  validateForm,
  validateParam,
  PublishedComponent,
  htmlTranslation,
  Sites,
  Routes,
  goToRoute,
  cookie,
  extractErrorMessage,
  analytics,
  Assets
} from '@pdrj/pmi-iqos-shared-react'
import { api, IqosphereAnalytics } from '@iqosphere-common/services'
import {
  getInitialPropsMock,
  buildProfileInfoData,
  dispatchFullyEarnedEvent,
  mapPrizeToProduct,
  fillPrizesWithExtraData
} from '@iqosphere-common/utils'
import LotteryPopup from './popup'
import { ProductDetailContent } from '@iqosphere-common/components'
import LotteryProductDetailTranslations from './lottery-product-detail.json'
import isMobile from 'ismobilejs'

const LotteryProductDetailComponent = ({
  translations,
  productDetail,
  confirmationUrl,
  redirectionUrl,
  terms,
  lotteryStateIcons,
  backgroundColor,
  popups,
  seoname,
  productid,
  variantid,
  reqProps
}) => {
  const states = {
    default: 'default',
    poor: 'poor',
    empty: 'empty',
    loose: 'loose',
    win: 'win',
    expired: 'expired'
  }

  const [productItem, setProductItem] = useState({})
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null)
  const [isSizeGuideShown, setIsSizeGuideShown] = useState(false)
  const [selectedSizeMap, setSelectedSizeMap] = useState({})
  const [loading, setLoading] = useState(false)
  const [formId, setFormId] = useState(null)
  const [sizeValidationParam, setSizeValidationParam] = useState(null)
  const [sizeErrors, setSizeErrors] = useState([])
  const [isFormValid, setIsFormValid] = useState(false)
  const [earnedQoins, setEarnedQoins] = useState(0)
  const [lotteryState, setLotteryState] = useState(states.default)
  const [orderId, setOrderId] = useState('')
  const [popupOpen, setPopupOpen] = useState(false)

  const t4UserRegStatus = '4'
  const sections = {
    exchange: 'exchange',
    lottery: 'lottery'
  }

  const sizeFieldName = 'size'

  const prizeName =
    productItem && productItem.product && productItem.product.loyalty.name

  const globalLoyalty =
    productItem && productItem.product && productItem.product.loyalty

  const eventCategory =
    globalLoyalty && globalLoyalty.section === sections.lottery
      ? 'Lottery'
      : globalLoyalty && globalLoyalty.section === sections.exchange
      ? 'Present'
      : ''

  const category = globalLoyalty && globalLoyalty.section
  const popupToast = ToastContext.getContextActions().popupToast

  useEffect(() => {
    async function init() {
      setLoading(true)
      try {
        if (
          productDetail &&
          productDetail.product.options &&
          productDetail.product.options.length > 0
        ) {
          const newProductDetail = await getPrizeExtraData()
          const updatedProductDetail = { ...newProductDetail }

          const defaultSelectedOptionIndex =
            updatedProductDetail.product.selectedOptionIndex
          const sizeMap = {}
          // Init size map if sizes are present
          updatedProductDetail.product.options.forEach((option, index) => {
            let hasOptionSize = ''
            option.loyalty.sizes.forEach(size => {
              hasOptionSize = size.value || ''
            })
            sizeMap[index] = hasOptionSize ? null : '' // null === sizes are available to select | '' === no sizes are available
          })
          // If first option has sizes, init form otherwise form is valid
          if (sizeMap[defaultSelectedOptionIndex] === null) {
            initForm(updatedProductDetail, defaultSelectedOptionIndex, sizeMap)
          } else {
            setIsFormValid(true)
          }
          setSelectedOptionIndex(defaultSelectedOptionIndex)
          setProductItem({ ...updatedProductDetail })
          setSelectedSizeMap({ ...sizeMap })
          await updateStateOnDataChanged(
            updatedProductDetail,
            defaultSelectedOptionIndex,
            sizeMap
          )
        }
      } catch (ex) {
        handleError(ex)
      } finally {
        setLoading(false)
        scroll.scrollToTop({
          duration: 500
        })
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkStock = (product, newSelectedSizeMap, selectedOptionIndex) => {
    const option = product.product.options[selectedOptionIndex]

    // If loyalty sizes exist, check the selected size stock
    if (option.loyalty?.sizes?.length > 0) {
      const currentSizeStock =
        option.loyalty.sizes.find(
          size => size.value === newSelectedSizeMap?.[selectedOptionIndex]
        )?.stock ?? 0

      return currentSizeStock > 0
    }
    return option.stock > 0
  }

  const getPrizeExtraData = async () => {
    let res = null
    try {
      if (seoname) {
        res = await api.iqospherePrize.getPrizeBySeoName(seoname, true)
      } else {
        res = await api.iqospherePrize.getPrize(productid, variantid, true)
      }
    } catch (err) {
      setLotteryState(states.empty)
    }
    if (res) {
      const updatedProduct = fillPrizesWithExtraData(
        [productDetail.product],
        res.prizes
      )
      return { product: updatedProduct[0] }
    }
  }

  const handleError = err => {
    const message = extractErrorMessage(err, translations.tk_error)
    popupToast({
      type: ToastContext.types.error,
      subtitle: message
    })
    const errorCode = err && err.data && err.data[0] && err.data[0].code
    switch (errorCode) {
      case '1004':
        setLotteryState(states.expired)
        break
      default:
        break
    }
  }

  const hasProductExchangeStock = async (item, optionIndex, sizeMap) => {
    if (item?.product?.isDigital) {
      const selectedOption =
        item?.product?.options && item.product.options[optionIndex]
      if (!selectedOption.hasStockDigitalVouchers) return false
    }
    const hasLocalStock = checkStock(item, sizeMap, optionIndex)
    if (!hasLocalStock) {
      return false
    }
    const prizeId = getPrizeId(item, optionIndex, sizeMap)
    if (prizeId) {
      const res = await api.iqosphereLottery.getExchangeStock(prizeId)
      return res.stock
    }
  }

  const checkIfNoStock = async (item, optionIndex, sizeMap) => {
    if (sizeMap[optionIndex] !== null) {
      const section = getCurrentSection(item)
      if (section === sections.exchange || section === sections.lottery) {
        const hasStock = await hasProductExchangeStock(
          item,
          optionIndex,
          sizeMap
        )
        if (!hasStock) {
          return true
        }
      }
    }
    return false
  }

  const getAdressConfirmationUrl = () =>
    `${confirmationUrl}?orderId=${orderId}&loyaltyname=${prizeName}&loyaltysection=${eventCategory}&callredirectUrl=${redirectionUrl}`

  const getQoins = async () => {
    const loyaltyCookieContent = cookie.getLoyaltyProfile()
    const p = loyaltyCookieContent && loyaltyCookieContent.p
    const profileInfoData = buildProfileInfoData(p)
    const qoins = profileInfoData && profileInfoData.ownedQoins
    return qoins
  }

  const checkIfNotEnoughQoins = async (item, optionIndex) => {
    const qoins = await getQoins()
    if (qoins < item.product.options[optionIndex].qoins) {
      return true
    }
    return false
  }

  const initForm = (item, selectedIndex, sizeMap) => {
    const value = sizeMap[selectedIndex]
    const id = registerForm()
    const sizeValues = item.product.options[selectedIndex].loyalty.sizes.map(
      size => size.value
    )
    const param =
      sizeValues.length > 0
        ? registerParam(null, false, id, value, errors => {
            setSizeErrors(errors)
          })
        : null
    setSizeValidationParam(param)
    setFormId(id)
    return id
  }

  const handleOptionChange = async index => {
    setLoading(true)
    setLotteryState(states.default)
    let isValid = false
    // No sizes available, no form validation needed
    if (selectedSizeMap[index] === '') {
      isValid = true
      // Size selected or not selected yet
    } else if (selectedSizeMap[index] || selectedSizeMap[index] === null) {
      const id = initForm(productItem, index, selectedSizeMap)
      isValid = validateForm(id)
      setSizeErrors([])
    }
    setIsSizeGuideShown(false)
    setSelectedOptionIndex(index)
    if (isValid) {
      try {
        await updateStateOnDataChanged(productItem, index, selectedSizeMap)
      } catch (ex) {
        handleError(ex)
      }
    }
    setIsFormValid(isValid)
    setLoading(false)
  }

  const handleSizeChange = async e => {
    setLoading(true)
    setLotteryState(states.default)
    const value = e.target.value || null // null means unselected
    const newSelectedSizeMap = {
      ...selectedSizeMap,
      [selectedOptionIndex]: value
    }
    setSelectedSizeMap(newSelectedSizeMap)
    if (sizeValidationParam) {
      const errors = validateParam(value, sizeValidationParam, formId)
      setSizeErrors(errors)
      if (errors.length === 0) {
        try {
          await updateStateOnDataChanged(
            productItem,
            selectedOptionIndex,
            newSelectedSizeMap
          )
        } catch (ex) {
          handleError(ex)
        }
      }
    }
    const isValid = validateForm(formId)
    setIsFormValid(isValid)
    setLoading(false)
  }

  const getPrizeId = (item, optionIndex, sizeMap) => {
    const selectedOption =
      item?.product?.options && item.product.options[optionIndex]
    // Return the prize code related to size
    if (sizeMap && sizeMap[optionIndex]) {
      const selectedSize = sizeMap && sizeMap[optionIndex]
      const size = selectedOption.loyalty.sizes.filter(
        size => size.value === selectedSize
      )[0]
      const prizeId = size && size.id
      return prizeId
    }
    // Return the prize code related to option
    return selectedOption.prizeCode
  }

  const getCurrentSection = item => {
    const section = item?.product?.loyalty?.section
    return section
  }

  const updateStateOnDataChanged = async (item, optionIndex, sizeMap) => {
    const noStock = await checkIfNoStock(item, optionIndex, sizeMap)
    if (noStock) {
      setLotteryState(states.empty)
      return true
    } else {
      if (await checkIfNotEnoughQoins(item, optionIndex)) {
        setLotteryState(states.poor)
        return true
      } else {
        setLotteryState(states.default)
      }
    }

    return false
  }

  const handleProductSelection = async () => {
    setLoading(true)

    switch (lotteryState) {
      case states.win:
        analytics.clickEvent({
          eventCategory: `Win ${eventCategory}`,
          eventLabel: `${prizeName} - Proceed to delivery`,
          user_id: cookie.getSpiceId() || ''
        })

        break
      case states.loose:
        analytics.clickEvent({
          eventCategory: 'Lottery',
          eventLabel: `${prizeName} - Try again`,
          user_id: cookie.getSpiceId() || ''
        })

        break
      case states.poor:
        analytics.clickEvent({
          eventCategory,
          eventLabel: `${prizeName} - How to earn Qoins`
        })
        break
      case states.empty:
        break
      default:
        analytics.clickEvent({
          eventCategory,
          eventLabel: `${prizeName} - Apply to exchange`,
          user_id: cookie.getSpiceId() || ''
        })
        break
    }
    try {
      const prizeId = getPrizeId(
        productItem,
        selectedOptionIndex,
        selectedSizeMap
      )
      if (lotteryState === states.default || lotteryState === states.loose) {
        const stateUpdated = await updateStateOnDataChanged(
          productItem,
          selectedOptionIndex,
          selectedSizeMap
        )
        if (!stateUpdated) {
          if (productItem.product.isDigital) {
            const reserved = await reserveDigitalPrize(prizeId)
            if (!reserved) return
          }
          let res = null
          res = await api.iqosphereLottery.confirm(prizeId)
          if (res.win) {
            setLotteryState(states.win)
            dispatchFullyEarnedEvent()
            setOrderId(res.orderId)
            IqosphereAnalytics.onCloseLotteryResult({
              eventLabel: `IQOS ${productItem.product.name} - Win`
            })
          } else {
            setEarnedQoins(res.earnedQoins)
            setLotteryState(states.loose)
            IqosphereAnalytics.onCloseLotteryResult({
              eventLabel: `IQOS ${productItem.product.name} - Lose`
            })
          }
          setPopupOpen(true)
        }
      }
      if (lotteryState === states.poor) {
        goToRoute({
          mainPath: Routes.iqospherePackCodesHowToUse,
          site: Sites.iqosphere
        })
      }
      if (lotteryState === states.win) {
        const profileInfo = cookie.getTokenInfo()
        if (profileInfo.reg_status === t4UserRegStatus) {
          goToRoute({
            mainPath: getAdressConfirmationUrl(),
            site: Sites.iqosphere
          })
        } else {
          goToRoute({
            mainPath: Routes.conversion,
            isBaseURL: true,
            params: [
              `callredirectUrl=${encodeURIComponent(
                getAdressConfirmationUrl()
              )}`
            ],
            site: Sites.iqosphere
          })
        }
      }
    } catch (ex) {
      handleError(ex)
    } finally {
      setLoading(false)
    }
  }

  const reserveDigitalPrize = async prizeId => {
    try {
      await api.iqosphereLottery.reserveDigitalPrize(prizeId)
      return true
    } catch (err) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: 'Error reserving digital prize'
      })
      setLotteryState(states.empty)
      return false
    }
  }

  const handleToggleShowSizeGuide = () => {
    setIsSizeGuideShown(!isSizeGuideShown)
  }

  const handleOnPackCodeClick = () => {
    analytics.onPackCodeClick('Application Terms')
  }

  const renderLotteryStateIcons = icon => {
    return (
      <Container width="14" height="14" overflowHidden>
        <Image src={icon} alt={icon} />
      </Container>
    )
  }

  const option =
    productItem &&
    productItem.product &&
    productItem.product.options &&
    productItem.product.options[selectedOptionIndex]
  const optionLoyalty = option && option.loyalty
  let buttonText = null
  switch (lotteryState) {
    case states.poor:
      buttonText = translations.tk_waysToEarnQoins
      break
    case states.loose:
      buttonText = translations.tk_tryAgain
      break
    case states.win:
      buttonText = translations.tk_confirmAddress
      break
    case states.empty:
    case states.default:
    default:
      buttonText = translations.tk_apply
  }

  const hideVariants =
    productItem?.product?.options?.length === 1 &&
    !productItem?.product?.options[0].hexColor

  const handleOnPopupClose = () => {
    setPopupOpen(false)
  }

  return (
    globalLoyalty &&
    optionLoyalty && (
      <>
        <BlockingLoader fullScreen loading={loading} altOpacity>
          <Image
            src={
              category === 'exchange'
                ? Assets.urls.exchangeLoadingAnimation
                : Assets.urls.lotteryLoadingAnimation
            }
            additionalClasses={`q-absolute q-inset-0 q-m-auto ${
              isMobile().any ? 'q-w-3/5' : 'q-w-1/5'
            }`}
          />
        </BlockingLoader>
        <Container pb="8">
          <Button
            icon={Icon.images.arrowLeft}
            iconPosition={Button.iconPositions.left}
            onClickHandler={() => {
              goToRoute({
                mainPath:
                  (productItem.product && productItem.product.campaignUrl) ||
                  Routes.iqospherePointsPresent,
                site: Sites.iqosphere
              })
            }}
            text={translations.tk_goBack}
            variant={Button.variants.tertiary}
            data-testing="LotteryProductDetail_Button_GoToBack"
          />
        </Container>
        <ProductDetailContent
          product={productItem.product}
          options={productItem.product.options}
          selectedOptionIndex={selectedOptionIndex}
          loading={loading}
          colorChangeHandler={handleOptionChange}
          translations={translations}
          loadingButton={loading}
          alternativeQoins={globalLoyalty.section === sections.exchange}
          disclaimerImage={optionLoyalty.disclaimerImage}
          showVariants={!hideVariants}
          backgroundColor={backgroundColor}
          sizeSelector={
            optionLoyalty.sizes &&
            optionLoyalty.sizes.map(size => size.value).filter(Boolean).length >
              0 ? (
              <Container fullWidth={[true, false]} pt="4">
                <Select
                  value={selectedSizeMap[selectedOptionIndex] || ''}
                  name={sizeFieldName}
                  label={translations.tk_sizeChoice}
                  canBeUnselected
                  unselectedLabel={translations.tk_selectSize}
                  items={optionLoyalty.sizes}
                  getItemLabel={index => optionLoyalty.sizes[index].value}
                  getItemValue={index => optionLoyalty.sizes[index].value}
                  onChangeHandler={handleSizeChange}
                  dataTesting="ProductDetailContent_Select_Size"
                  errors={sizeErrors}
                />
              </Container>
            ) : null
          }>
          {globalLoyalty.description ? (
            <Container pt="4">
              <TextBody acceptsHtml value={globalLoyalty.description} />
            </Container>
          ) : null}
          {globalLoyalty.disclaimer ? (
            <Container pt="4">
              <TextBody
                size={TextBody.sizes.s}
                acceptsHtml
                value={globalLoyalty.disclaimer}
              />
            </Container>
          ) : null}
          <Container pt="8" fullWidth>
            {lotteryState === states.poor && (
              <Container
                pb="4"
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.center}>
                <Container pr="2">
                  <State
                    state={State.states.warningRed}
                    size={State.sizes.xs}
                    data-testing="LotteryProductDetail_State_InsufficientQoins"
                  />
                </Container>
                <TextBody
                  size={TextBody.sizes.s}
                  style={TextBody.styles.error}
                  {...htmlTranslation(translations, 'tk_insufficientPoints')}
                />
              </Container>
            )}
            {lotteryState === states.loose && (
              <Container
                pb="8"
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.center}>
                <Container pr="4">
                  {lotteryStateIcons && lotteryStateIcons.loose ? (
                    renderLotteryStateIcons(lotteryStateIcons.loose)
                  ) : (
                    <Icon image={Icon.images.iqosPicto} size={Icon.sizes.l} />
                  )}
                </Container>
                <Container
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.column}
                  justifyContent={Container.justifyContentValues.center}>
                  <TextBody
                    style={TextBody.styles.grayedout}
                    {...htmlTranslation(translations, 'tk_noPrize')}
                  />
                  <Container pt="2" display={Container.displays.flex}>
                    <Container pr="1">
                      <TextCaption size={TextCaption.sizes.s}>
                        {earnedQoins}
                      </TextCaption>
                    </Container>
                    <TextCaption size={TextCaption.sizes.s}>
                      {translations.tk_qoinsWon}
                    </TextCaption>
                  </Container>
                </Container>
              </Container>
            )}
            {lotteryState === states.win && (
              <>
                <Container
                  pb="8"
                  display={Container.displays.flex}
                  alignItems={Container.alignItemsValues.center}>
                  <Container pr="4">
                    {globalLoyalty.section === sections.lottery ? (
                      lotteryStateIcons && lotteryStateIcons.win_lottery ? (
                        renderLotteryStateIcons(lotteryStateIcons.win_lottery)
                      ) : (
                        <Icon64
                          image={Icon64.images.rewards2}
                          size={Icon.sizes.l}
                        />
                      )
                    ) : lotteryStateIcons && lotteryStateIcons.win_exchange ? (
                      renderLotteryStateIcons(lotteryStateIcons.win_exchange)
                    ) : (
                      <Icon64
                        image={Icon64.images.confirmation}
                        size={Icon.sizes.l}
                      />
                    )}
                  </Container>
                  <TextTitle heading={TextTitle.headings.h5}>
                    {globalLoyalty.section === sections.lottery
                      ? translations.tk_congratulations
                      : translations.tk_exchangeSuccessful}
                  </TextTitle>
                </Container>
                <Container pb="8">
                  <TextBody
                    size={TextBody.sizes.s}
                    {...htmlTranslation(
                      translations,
                      'tk_pleaseConfirmAddress'
                    )}
                  />
                </Container>
              </>
            )}
            {lotteryState === states.empty && (
              <Container pb="8">
                <Container p="4" bg={Container.backgrounds.hardWhite}>
                  <TextCaption size={TextCaption.sizes.s}>
                    {translations.tk_outOfStockTitle}
                  </TextCaption>
                  <Container pt="2">
                    <TextBody
                      size={TextBody.sizes.s}
                      {...htmlTranslation(translations, 'tk_outOfStockMessage')}
                    />
                  </Container>
                </Container>
              </Container>
            )}
            <ListContainer
              columns={[
                1,
                1,
                lotteryState === states.loose &&
                globalLoyalty.pdpCta &&
                globalLoyalty.pdpCta.url &&
                globalLoyalty.pdpCta.label
                  ? 2
                  : 1
              ]}
              yBetween="4"
              xBetween="4">
              <Button
                fullWidth={!Tools.isDesktop()}
                onClickHandler={handleProductSelection}
                text={buttonText}
                disabled={
                  lotteryState !== states.poor &&
                  (lotteryState === states.expired ||
                    lotteryState === states.empty ||
                    !isFormValid)
                }
              />
              {lotteryState === states.loose &&
                globalLoyalty.pdpCta &&
                globalLoyalty.pdpCta.url &&
                globalLoyalty.pdpCta.label && (
                  <Button
                    fullWidth={!Tools.isDesktop()}
                    onClickHandler={() => {
                      analytics.clickEvent({
                        eventCategory: 'Lottery',
                        eventLabel: `${prizeName} - Purchase the Product`,
                        user_id: cookie.getSpiceId() || ''
                      })
                      goToRoute({
                        mainPath: globalLoyalty.pdpCta.url
                      })
                    }}
                    text={globalLoyalty.pdpCta.label}
                    variant={Button.variants.secondary}
                  />
                )}
            </ListContainer>
            {lotteryState === states.loose &&
              globalLoyalty.extraCta &&
              globalLoyalty.extraCta.url &&
              globalLoyalty.extraCta.label && (
                <Container pt={['6', '6', '8']}>
                  <Button
                    variant={Button.variants.tertiary}
                    text={globalLoyalty.extraCta.label}
                    onClickHandler={() => {
                      goToRoute({
                        mainPath: globalLoyalty.extraCta.url
                      })
                    }}
                    icon={Icon.images.arrowRight}
                    iconPosition={Button.iconPositions.right}
                  />
                </Container>
              )}
          </Container>
          {globalLoyalty.sizeGuide ? (
            <Container pt="8" fullWidth>
              <ExpandableButton
                onClickHandler={handleToggleShowSizeGuide}
                icon={
                  isSizeGuideShown
                    ? Icon.images.chevronUp
                    : Icon.images.chevronDown
                }
                iconPosition={ExpandableButton.iconPositions.right}
                textTemplate={
                  <TextCaption>{translations.tk_sizeGuide}</TextCaption>
                }
                data-testing="ProductBundleDetail_Button_ReadMore"
              />
              {isSizeGuideShown && (
                <Container pt="2">
                  <HtmlContent fullWidth content={globalLoyalty.sizeGuide} />
                </Container>
              )}
            </Container>
          ) : null}
          {lotteryState !== states.win ? (
            <>
              {terms && (
                <Container pt="8">
                  <Link onClickHandler={handleOnPackCodeClick} href={terms}>
                    {translations.tk_termsAndConditions}
                  </Link>
                </Container>
              )}
              {translations.tk_disclaimer2 ? (
                <Container pt="4">
                  <TextBody
                    size={TextBody.sizes.s}
                    {...htmlTranslation(translations, 'tk_disclaimer2')}
                  />
                </Container>
              ) : null}
            </>
          ) : (
            <Container pt="8">
              <TextCaption>{translations.tk_confirmAddressNote}</TextCaption>
              <Container pt="2">
                <TextBody
                  size={TextBody.sizes.s}
                  {...htmlTranslation(
                    translations,
                    'tk_confirmAddressDisclaimer'
                  )}
                />
              </Container>
            </Container>
          )}
        </ProductDetailContent>
        <LotteryPopup
          handleOnPopupClose={handleOnPopupClose}
          lotteryState={lotteryState}
          popupOpen={popupOpen}
          popups={popups}
          states={states}
          translations={translations}
        />
      </>
    )
  )
}

const LotteryProductDetail = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={LotteryProductDetailComponent}
        mainLayout
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...LotteryProductDetailTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'LotteryProductDetail',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

LotteryProductDetail.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const seoname =
        req && req.query && req.query.mock === 'true'
          ? 'bau-test'
          : (req.query && req.query.seoname) || ''
      const productid =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('productId')
          : (req.query && req.query.productid) || ''
      const variantid =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('variantId')
          : (req.query && req.query.variantid) || ''
      const confirmationUrl =
        req && req.query && req.query.mock === 'true'
          ? '/'
          : (req.body.extra && req.body.extra.confirmationUrl) || null
      const redirectionUrl =
        req && req.query && req.query.mock === 'true'
          ? '/'
          : (req.body.extra && req.body.extra.redirectionUrl) || null
      const terms =
        req && req.query && req.query.mock === 'true'
          ? '/'
          : (req.body.extra && req.body.extra.tandc) || null
      const lotteryStateIcons =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('lotteryStateIcons')
          : (req.body.extra && req.body.extra.lottery_state_icons) || null
      const backgroundColor =
        (req.body.extra && req.body.extra.background_color) ||
        PublishedComponent.backgrounds.white
      const popups =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('popupWinOrLose')
          : (req.body.extra && req.body.extra.popups) || ''

      let result
      try {
        result = await LotteryProductDetail.getProduct(
          seoname,
          productid,
          variantid
        )
        if (result.error) return result
      } catch (error) {
        throw error
      }

      const productDetail = await LotteryProductDetail.getProductInfo(result)

      const selectedVariant =
        result &&
        result.options &&
        result.options.length > 0 &&
        result.options[result.selectedVariant]

      return {
        extras: {
          name: selectedVariant?.variantName || null,
          product_id: productid || null,
          seo_title: result.seoTitle,
          seo_description: result.seoDescription
        },
        productDetail,
        confirmationUrl,
        redirectionUrl,
        terms,
        lotteryStateIcons,
        backgroundColor,
        seoname,
        productid,
        variantid,
        reqProps: {
          query: req.query,
          extra: req.body.extra
        },
        popups
      }
    }
  })
}

LotteryProductDetail.getProductInfo = async product => {
  return mapPrizeToProduct(product)
}

LotteryProductDetail.getProduct = async (seoname, productid, variantid) => {
  let res = null
  try {
    if (seoname) {
      res = await api.iqospherePrize.getPrizeBySeoName(seoname)
    } else {
      res = await api.iqospherePrize.getPrize(productid, variantid)
    }
  } catch (err) {
    const message = extractErrorMessage(err)
    // since API GW does not send us a proper 404 we need to treat in a special way this error
    // in order for Drupal to send a proper 404
    if (message === 'Product not available') {
      return LotteryProductDetail.getProductErrorObj(message)
    } else {
      throw err
    }
  }

  const result = res?.prizes[0]

  if (!result) {
    return LotteryProductDetail.getProductErrorObj('Product not available')
  }
  return result
}

LotteryProductDetail.getProductErrorObj = message => ({
  error: {
    message,
    status: 404
  }
})

export default LotteryProductDetail
